import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Download, Search, X, Trash2 } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "archived"];
const STATUS_STYLE = {
  new: "bg-restrained-blue/10 text-restrained-blue",
  contacted: "bg-amber-100 text-amber-800",
  qualified: "bg-green-100 text-green-800",
  archived: "bg-charcoal/10 text-charcoal/60",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { status, source, q } });
      setLeads(data);
    } catch { toast.error("Failed to load leads"); }
    setLoading(false);
  }, [status, source, q]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/leads/${id}/status`, { status: newStatus });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status: newStatus }));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/admin/leads/${selected.id}/notes`, { text: note });
      const updated = { ...selected, notes: [...(selected.notes || []), data.note] };
      setSelected(updated);
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? updated : l)));
      setNote("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = () => {
    window.open(`${API}/admin/leads/export`, "_blank");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-4xl text-deep-navy">Leads</h1><p className="text-charcoal/60">{leads.length} shown</p></div>
        <button onClick={exportCsv} data-testid="leads-export" className="inline-flex items-center gap-2 border border-deep-navy text-deep-navy px-5 py-2.5 text-sm hover:bg-deep-navy hover:text-white transition-colors">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company"
                 className="w-full border border-[#E5E5E5] bg-white pl-10 pr-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue" />
        </div>
        <select data-testid="leads-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="leads-filter-source" value={source} onChange={(e) => setSource(e.target.value)} className="border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm">
          <option value="all">All sources</option>
          <option value="contact">Contact</option>
          <option value="process">Process</option>
          <option value="ai-assistant">AI Assistant</option>
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-off-white border-b border-[#E5E5E5]">
            <tr className="text-left text-charcoal/50">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium hidden md:table-cell">Company</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium hidden md:table-cell">Received</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-charcoal/40">Loading…</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-charcoal/40" data-testid="leads-empty">No leads found.</td></tr>
            ) : leads.map((l) => (
              <tr key={l.id} className="border-b border-[#E5E5E5] hover:bg-off-white cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3"><p className="font-medium text-charcoal">{l.name}</p><p className="text-charcoal/50 text-xs">{l.email}</p></td>
                <td className="px-4 py-3 hidden md:table-cell text-charcoal/70">{l.company || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-wide text-charcoal/60">{l.source}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 ${STATUS_STYLE[l.status]}`}>{l.status}</span></td>
                <td className="px-4 py-3 hidden md:table-cell text-charcoal/50 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); remove(l.id); }} className="text-charcoal/30 hover:text-destructive"><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center p-6 border-b border-[#E5E5E5] sticky top-0 bg-white">
              <h2 className="font-serif text-2xl text-deep-navy">{selected.name}</h2>
              <button onClick={() => setSelected(null)} data-testid="lead-detail-close" className="text-charcoal/50 hover:text-charcoal"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="overline text-charcoal/40">Email</p><a href={`mailto:${selected.email}`} className="text-restrained-blue break-all">{selected.email}</a></div>
                <div><p className="overline text-charcoal/40">Company</p><p>{selected.company || "—"}</p></div>
                <div><p className="overline text-charcoal/40">Role</p><p>{selected.role_title || "—"}</p></div>
                <div><p className="overline text-charcoal/40">Source</p><p className="capitalize">{selected.source}</p></div>
              </div>
              <div>
                <p className="overline text-charcoal/40 mb-2">Status</p>
                <div className="flex gap-2 flex-wrap">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => updateStatus(selected.id, s)} data-testid={`status-btn-${s}`}
                            className={`text-xs px-3 py-1.5 border transition-colors ${selected.status === s ? "bg-deep-navy text-white border-deep-navy" : "border-[#E5E5E5] text-charcoal/70 hover:border-restrained-blue"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-charcoal/40 mb-2">Message</p>
                <p className="text-charcoal/80 text-sm leading-relaxed whitespace-pre-wrap bg-off-white p-4 border border-[#E5E5E5]">{selected.message}</p>
              </div>
              <div>
                <p className="overline text-charcoal/40 mb-2">Notes</p>
                <div className="space-y-2 mb-3">
                  {(selected.notes || []).map((n, i) => (
                    <div key={i} className="text-sm bg-off-white p-3 border border-[#E5E5E5]"><p className="text-charcoal/80">{n.text}</p><p className="text-charcoal/40 text-xs mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p></div>
                  ))}
                  {(!selected.notes || selected.notes.length === 0) && <p className="text-sm text-charcoal/40">No notes yet.</p>}
                </div>
                <div className="flex gap-2">
                  <input data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" className="flex-1 border border-[#E5E5E5] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue" />
                  <button onClick={addNote} data-testid="lead-note-add" className="bg-deep-navy text-white px-4 text-sm hover:bg-restrained-blue transition-colors">Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
